import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import companiesData from './companies.json';
import { Container, Row, Col, InputGroup, FormControl, Badge, Button } from 'react-bootstrap';
import {
  HiSearch,
  HiX,
  HiSparkles,
  HiOutlineTag,
  HiArrowNarrowRight,
  HiFire,
  HiGift,
  HiShieldCheck
} from './components/Icons';
import SEO from './components/SEO';
import { slugifyCategory } from './utils/categories';
import { trackCategoryFilter, trackSearch } from './utils/analytics';
import { useLanguage } from './context/LanguageContext';
import './Home.css';
import { CompanyType } from './types';

const companies: CompanyType[] = companiesData;

// Extract unique categories
const CATEGORIES = ['Alle', ...Array.from(new Set(companies.map(c => c.category || 'Sonstiges')))];

function Home() {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  // Filter companies by search query and category
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch =
        company.name.toLowerCase().includes(search.toLowerCase()) ||
        company.coupon.value.toLowerCase().includes(search.toLowerCase()) ||
        company.coupon.description.toLowerCase().includes(search.toLowerCase()) ||
        (company.category && company.category.toLowerCase().includes(search.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'Alle' || company.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory]);

  // Debounced search event tracking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim().length > 1) {
        trackSearch(search, filteredCompanies.length);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [search, filteredCompanies.length]);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    trackCategoryFilter(category);
  };

  // Group filtered companies by alphabetical letter / category
  const sortedCompanies = useMemo(() => {
    const grouped: { [key: string]: CompanyType[] } = {
      '1-9': [],
      '#': [],
      ...'abcdefghijklmnopqrstuvwxyz'.split('').reduce((acc, letter) => {
        acc[letter] = filteredCompanies.filter(company =>
          company.name.toLowerCase().startsWith(letter)
        );
        return acc;
      }, {} as Record<string, CompanyType[]>)
    };

    filteredCompanies.forEach(company => {
      const firstChar = company.name.charAt(0);
      if (firstChar.match(/[1-9]/)) {
        grouped['1-9'].push(company);
      } else if (!firstChar.match(/[a-z]/i)) {
        grouped['#'].push(company);
      }
    });

    return Object.entries(grouped).filter(([_, items]) => items.length > 0);
  }, [filteredCompanies]);

  // Quick initial avatar generator
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const structuredData = useMemo(() => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://couponshowcase.com/#website",
        "url": "https://couponshowcase.com/",
        "name": "Coupon Showcase - Handverlesene Gutscheine & Rabatte",
        "description": "Entdecken Sie exklusive Gutscheine, Rabattcodes und Empfehlungsprämien für Top-Marken wie American Express, Uber Eats, N26, Grover und mehr.",
        "inLanguage": "de-DE",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://couponshowcase.com/?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Aktuelle Gutscheine & Sparangebote",
        "description": "Handverlesene Rabattcodes und Prämien 2026",
        "itemListElement": companies.map((c, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "name": `${c.name} - ${c.coupon.value}`,
          "url": `https://couponshowcase.com/${c.name.replace(/\s+/g, '_').toLowerCase()}`
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Wie löse ich einen Gutscheincode auf Coupon Showcase ein?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Wähle das gewünschte Angebot aus. Klicke auf 'Gutscheincode kopieren', um den Code in die Zwischenablage zu übernehmen, und füge ihn beim Bezahlvorgang im Shop ein. Bei Einladungslinks leitet dich der Button direkt zum Bonusangebot weiter."
            }
          },
          {
            "@type": "Question",
            "name": "Sind alle Gutscheine und Einladungslinks kostenlos nutzbar?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, alle Gutscheine, Rabattcodes und Empfehlungs-Prämien auf Coupon Showcase sind zu 100% kostenlos und ohne Anmeldung oder Registrierung nutzbar."
            }
          },
          {
            "@type": "Question",
            "name": "Wie sichere ich mir bis zu 85.000 Punkte bei American Express Platinum?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Klicke auf das American Express Platinum Angebot, folge dem offiziellen Einladungslink und beantrage die Karte online. Nach Erreichen des Mindestumsatzes innerhalb der ersten 6 Monate werden dir bis zu 85.000 Membership Rewards® Punkte gutgeschrieben."
            }
          },
          {
            "@type": "Question",
            "name": "Wie oft werden die Gutscheincodes und Rabatte geprüft?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Unser Portfolio wird kontinuierlich geprüft und aktualisiert, damit dir stets aktuelle, gültige Gutscheincodes und geprüfte Prämien zur Verfügung stehen."
            }
          }
        ]
      }
    ];
  }, []);

  return (
    <div className="home-page">
      <SEO
        title="Coupon Showcase - Handverlesene Gutscheine & Rabatte 2026"
        description="Entdecken Sie exklusive Gutscheine, Rabattcodes und Weiterempfehlungs-Prämien für American Express, Uber Eats, N26, Grover, HelloFresh und mehr."
        canonical="/"
        keywords="Gutscheine, Rabatte, Rabattcodes, Coupons, Deals, Sparen, American Express Platinum Punkte, Uber Eats Gutschein, N26 Prämie, Grover Cash, HelloFresh, 2026"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-glow-bg"></div>
        <Container>
          <div className="hero-content text-center">
            <div className="hero-pill-badge mb-3">
              <HiFire className="text-warning me-1" /> DIE BESTEN SPAR-ANGEBOTE & PRÄMIEN
            </div>
            <h1 className="hero-title">
              Exklusive <span className="gradient-text">Gutscheine & Rabatte</span> für Top-Marken
            </h1>
            <p className="hero-subtitle">
              Entdecke geprüfte Aktionscodes, Willkommensboni und Weiterempfehlungs-Prämien für American Express, Uber Eats, N26, Grover und mehr.
            </p>

            {/* Quick Stats Counter */}
            <div className="hero-stats-row">
              <div className="stat-item">
                <HiGift className="stat-icon text-indigo" />
                <span><strong>{companies.length}</strong> Top-Partner</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <HiSparkles className="stat-icon text-amber" />
                <span>Bis zu <strong>85.000 Punkte</strong></span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <HiShieldCheck className="stat-icon text-emerald" />
                <span><strong>100%</strong> Kostenlos & Geprüft</span>
              </div>
            </div>

            {/* Search Input Box */}
            <div className="hero-search-wrapper">
              <InputGroup className="search-input-group">
                <InputGroup.Text className="search-icon-addon">
                  <HiSearch className="search-icon" />
                </InputGroup.Text>
                <FormControl
                  className="search-input text-center"
                  placeholder={t('searchPlaceholder', 'Shop Name suchen (z.B. American Express, Uber Eats, N26)...')}
                  aria-label="Shop Name"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <Button
                    variant="link"
                    className="search-clear-btn"
                    onClick={() => setSearch('')}
                    aria-label={t('clearSearch', 'Suche löschen')}
                  >
                    <HiX />
                  </Button>
                )}
              </InputGroup>
            </div>
          </div>
        </Container>
      </section>

      {/* Main Deals Container */}
      <Container className="deals-container py-4" id="categories">
        {/* Category Pills Filter Bar */}
        <div className="category-filter-bar mb-4">
          <div className="category-chips-scroll">
            {CATEGORIES.map(category => (
              <button
                key={category}
                type="button"
                className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategorySelect(category)}
              >
                {category}
                {category === 'Alle' ? (
                  <span className="chip-count">{companies.length}</span>
                ) : (
                  <span className="chip-count">
                    {companies.filter(c => c.category === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Alphabet Quick Navigation */}
        {sortedCompanies.length > 0 && (
          <div className="alphabet-nav mb-4">
            <span className="alphabet-nav-label">Index:</span>
            <div className="alphabet-nav-pills">
              {sortedCompanies.map(([letter]) => (
                <a key={letter} href={`#letter-${letter}`} className="alphabet-pill">
                  {letter.toUpperCase()}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="results-header d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="results-count d-flex align-items-center flex-wrap gap-2">
            <div>
              <HiOutlineTag className="me-1 text-primary" />
              <span>
                Zeige <strong>{filteredCompanies.length}</strong> {filteredCompanies.length === 1 ? 'Angebot' : 'Angebote'}
              </span>
            </div>
            {selectedCategory !== 'Alle' && (
              <Link
                to={`/kategorie/${slugifyCategory(selectedCategory)}`}
                className="btn btn-sm btn-outline-primary py-0 px-2 rounded-pill"
              >
                {selectedCategory} Übersichtsseite öffnen ➔
              </Link>
            )}
          </div>
          {(search || selectedCategory !== 'Alle') && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="reset-filter-btn"
              onClick={() => {
                setSearch('');
                setSelectedCategory('Alle');
              }}
            >
              Filter zurücksetzen
            </Button>
          )}
        </div>

        {/* Company Deals Grid grouped by Letter */}
        {sortedCompanies.length > 0 ? (
          sortedCompanies.map(([letter, items]) => (
            <div id={`letter-${letter}`} key={letter} className="letter-section mb-5">
              <div className="section-heading-wrapper">
                <h2 className="letter-heading">{letter.toUpperCase()}</h2>
                <div className="letter-heading-line"></div>
              </div>

              <Row className="g-4">
                {items.map((company, index) => {
                  const companySlug = company.name.replace(/\s+/g, '_').toLowerCase();
                  return (
                    <Col lg={4} md={6} sm={12} key={index}>
                      <div className="deal-card">
                        <div className="card-top-bar">
                          <div className="brand-avatar" style={{ background: company.color || '#4f46e5' }}>
                            {getInitials(company.name)}
                          </div>
                          <div className="card-tags">
                            {company.featured && (
                              <span className="featured-badge">
                                <HiFire className="me-1" /> Top Deal
                              </span>
                            )}
                            <Badge bg="light" text="dark" className="category-badge">
                              {company.category || 'Gutschein'}
                            </Badge>
                          </div>
                        </div>

                        <div className="card-content">
                          <h3 className="company-title">{company.name}</h3>
                          
                          <div className="deal-value-pill">
                            <span className="value-label">Vorteil:</span>
                            <span className="value-text">{company.coupon.value}</span>
                          </div>

                          <p className="card-description">{company.coupon.description}</p>
                        </div>

                        <div className="card-action-footer">
                          <span className="action-text">
                            {company.coupon.code ? 'Gutscheincode anzeigen' : 'Angebot einlösen'}
                          </span>
                          <span className="action-arrow">
                            <HiArrowNarrowRight />
                          </span>
                        </div>

                        {/* Stretched Clickable Overlay Link */}
                        <Link to={`/${companySlug}`} className="stretched-link" aria-label={company.name} />
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          ))
        ) : (
          /* Empty Search Results State */
          <div className="empty-search-state text-center py-5">
            <div className="empty-icon-wrapper mb-3">
              <HiSearch className="empty-search-icon" />
            </div>
            <h3 className="empty-title">Keine passenden Angebote gefunden</h3>
            <p className="empty-subtitle text-muted">
              Für deine Suche nach <strong>"{search}"</strong> konnten leider keine Gutscheine gefunden werden.
            </p>
            <Button
              variant="primary"
              className="mt-2 px-4 py-2"
              onClick={() => {
                setSearch('');
                setSelectedCategory('Alle');
              }}
            >
              Alle Gutscheine anzeigen
            </Button>
          </div>
        )}

        {/* Benefits / How It Works Section */}
        <section className="how-it-works-section mt-5 pt-4">
          <h2 className="section-title text-center mb-4">So funktioniert Coupon Showcase</h2>
          <Row className="g-4">
            <Col md={4}>
              <div className="benefit-card">
                <div className="benefit-number">1</div>
                <h4>Gutschein auswählen</h4>
                <p>Finde geprüfte Deals, Gutscheincodes und Weiterempfehlungs-Aktionen deiner Lieblingsmarken.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="benefit-card">
                <div className="benefit-number">2</div>
                <h4>Code kopieren oder Link nutzen</h4>
                <p>Mit nur einem Klick den Rabattcode in die Zwischenablage kopieren oder direkt zum Partner wechseln.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="benefit-card">
                <div className="benefit-number">3</div>
                <h4>Sofort sparen & profitieren</h4>
                <p>Gutschein beim Checkout einlösen und über Bonuspunkte, Guthaben oder Rabatte freuen!</p>
              </div>
            </Col>
          </Row>
        </section>

        {/* SEO FAQ Section */}
        <section className="faq-section mt-5 pt-4 pb-4" id="faq">
          <div className="section-heading-wrapper text-center mb-4">
            <h2 className="section-title">Häufig gestellte Fragen (FAQ)</h2>
            <p className="text-muted">Alles Wissenswerte über unsere geprüften Gutscheine, Rabattcodes und Prämien.</p>
          </div>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="faq-grid">
                <div className="faq-card">
                  <h3 className="faq-question">Wie löse ich einen Gutscheincode auf Coupon Showcase ein?</h3>
                  <p className="faq-answer">Wähle einfach das gewünschte Angebot aus. Bei Rabattcodes kannst du den Code mit einem Klick auf <strong>„Gutscheincode kopieren“</strong> direkt in deine Zwischenablage übernehmen und im Bestellprozess des Partner-Shops einfügen. Bei Einladungsaktionen leitet dich der <strong>„Angebot einlösen“</strong>-Button direkt zum Aktionslink weiter.</p>
                </div>
                <div className="faq-card">
                  <h3 className="faq-question">Sind alle Rabattcodes und Gutscheine kostenlos?</h3>
                  <p className="faq-answer">Ja, Coupon Showcase ist für alle Besucher zu 100% kostenlos und unverbindlich. Es ist keine Registrierung oder Angabe persönlicher Daten erforderlich, um die Gutscheincodes abzurufen.</p>
                </div>
                <div className="faq-card">
                  <h3 className="faq-question">Wie sichere ich mir bis zu 85.000 Punkte bei American Express?</h3>
                  <p className="faq-answer">Über unseren exklusiven American Express Platinum Empfehlungslink gelangst du direkt zum offiziellen Online-Kartenantrag. Bei Erreichen des Mindestumsatzes innerhalb der ersten 6 Monate werden dir bis zu 85.000 Membership Rewards® Punkte auf deinem Kartenkonto gutgeschrieben.</p>
                </div>
                <div className="faq-card">
                  <h3 className="faq-question">Wie oft werden neue Gutscheine und Angebote hinzugefügt?</h3>
                  <p className="faq-answer">Wir durchsuchen und aktualisieren unser Portfolio fortlaufend, um dir stets die attraktivsten Neukundenboni, Rabattcodes und Sparvorteile für Lifestyle, Banking, Food, Mobilität und Tech zur Verfügung zu stellen.</p>
                </div>
              </div>
            </Col>
          </Row>
        </section>
      </Container>
    </div>
  );
}

export default Home;
